import { useCallback, useEffect, useRef, useState } from 'react'

/// Valor que a pessoa edita na tela e que também acompanha o banco.
///
/// A tela responde na hora (estado local), e o banco recebe depois, pela gravação
/// com pausa. O difícil é o caminho de volta: quando o banco muda, é o eco do que
/// este campo mandou gravar, ou uma mudança de fora — outra aba, a descida do
/// servidor?
///
/// Aqui o campo lembra o que mandou e o banco ainda não devolveu. Valor que chega e
/// está nessa lista é eco, inclusive de um estado intermediário (a gravação da pausa
/// no meio da frase): fica o que está na tela. Valor que não está é de fora, e vale.
/// Antes era por tempo — ignorar o banco por 2 s depois da última tecla —, e uma
/// mudança de fora que caísse nessa janela nunca mais aparecia.
export function useValorEditavel<T>(
  externo: T,
  igual: (a: T, b: T) => boolean,
): [T, (novo: T) => void] {
  const [local, setLocal] = useState(externo)
  const aCaminho = useRef<T[]>([])
  const igualRef = useRef(igual)
  igualRef.current = igual

  useEffect(() => {
    const lista = aCaminho.current
    for (let i = lista.length - 1; i >= 0; i--) {
      if (igualRef.current(lista[i]!, externo)) {
        // Eco. O que foi mandado antes dele já chegou ou foi superado.
        aCaminho.current = lista.slice(i + 1)
        return
      }
    }
    aCaminho.current = []
    setLocal((atual) => (igualRef.current(atual, externo) ? atual : externo))
  }, [externo])

  const mudar = useCallback((novo: T) => {
    aCaminho.current.push(novo)
    // Só os mais recentes importam: o banco devolve em ordem.
    if (aCaminho.current.length > 50) aCaminho.current.shift()
    setLocal(novo)
  }, [])

  return [local, mudar]
}
